import { ref } from 'vue'
import type { Ref } from 'vue'
import { http } from '@/api/http'
import { getApiUrl } from '@/data/constants'

interface CacheEntry {
  available: boolean
  timestamp: number
}

const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes
const cache = ref<CacheEntry | null>(null)
const available = ref<boolean | null>(null)
const checking = ref(false)

/**
 * Check if the product_dl service is available.
 * Caches the result for 5 minutes to avoid excessive requests.
 * Returns a ref that is true if available, false if unavailable, null if unknown.
 */
export function useProductDlAvailable(): Ref<boolean | null> {
  const checkAvailability = async () => {
    const now = Date.now()

    // Return cached result if still valid
    if (cache.value && now - cache.value.timestamp < CACHE_DURATION) {
      available.value = cache.value.available
      return cache.value.available
    }

    // Prevent multiple concurrent checks
    if (checking.value) {
      return available.value ?? false
    }

    checking.value = true
    try {
      const response = await http.get(`${getApiUrl()}/import/health`)
      const isAvailable = response.data.available ?? false
      cache.value = { available: isAvailable, timestamp: now }
      available.value = isAvailable
      return isAvailable
    } catch (error) {
      cache.value = { available: false, timestamp: now }
      available.value = false
      return false
    } finally {
      checking.value = false
    }
  }

  // Trigger initial check
  checkAvailability()

  return available
}

/**
 * Returns the cached availability status synchronously.
 * Returns false if not yet checked or cache is expired.
 */
export function getProductDlAvailableSync(): boolean {
  if (!cache.value) return false
  if (Date.now() - cache.value.timestamp >= CACHE_DURATION) {
    return false
  }
  return cache.value.available
}

/**
 * Manually refresh the availability cache.
 */
export async function refreshProductDlAvailable(): Promise<boolean> {
  checking.value = true
  try {
    const response = await http.get(`${getApiUrl()}/import/health`)
    const isAvailable = response.data.available ?? false
    cache.value = { available: isAvailable, timestamp: Date.now() }
    available.value = isAvailable
    return isAvailable
  } catch (error) {
    cache.value = { available: false, timestamp: Date.now() }
    available.value = false
    return false
  } finally {
    checking.value = false
  }
}
